import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Truck, RotateCcw, ShieldCheck, Ruler, MessageCircle } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useFetch } from "../lib/api";
import { useShop } from "../lib/store";
import { chf } from "../lib/format";
import { toast } from "sonner";
import { Seo, SectionHead, Loading } from "../components/Common";
import { ProductRail, ProductGrid } from "../components/ProductCard";
import { useOutletContext } from "react-router-dom";

const TILES = ["beds", "mattresses", "wardrobes", "bedroom-sets", "sofas", "dining-tables", "storage", "desks"];

const Tile = ({ to, img, title, testid, tall }) => (
  <Link to={to} className="group block relative overflow-hidden bg-[color:var(--hw-beige)]" data-testid={testid}>
    <div className={tall ? "aspect-[3/4]" : "aspect-square"}><img src={img} alt={title} loading="lazy" className="w-full h-full object-cover img-zoom" /></div>
    <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/55 to-transparent"><p className="text-white font-serif text-xl sm:text-2xl">{title}</p></div>
  </Link>
);

export const BundleCard = ({ b }) => {
  const { t } = useI18n();
  const { addToCart } = useShop();
  return (
    <div className="border border-[color:var(--hw-line)] bg-white p-5 flex flex-col" data-testid={`bundle-${b.slug}`}>
      <div className="flex -space-x-6 mb-4">{b.items.map((i) => <img key={i.slug} src={i.image} alt={i.name} loading="lazy" className="w-24 h-28 object-cover border-4 border-white" />)}</div>
      <h3 className="font-serif text-2xl mb-2">{b.name}</h3>
      <ul className="text-sm text-[color:var(--hw-muted)] mb-4 space-y-1 flex-1">{b.items.map((i) => <li key={i.slug}>{i.qty} × <Link className="link-u" to={`/p/${i.slug}`}>{i.name}</Link> – {chf(i.price)}</li>)}</ul>
      <p className="text-sm"><span className="line-through text-[color:var(--hw-muted)] mr-2">{chf(b.total)}</span><span className="font-semibold text-lg">{chf(b.bundle_price)}</span></p>
      <p className="text-xs text-[color:var(--hw-green)] mb-4">{t("bundle_save", { s: chf(b.saving) })} (−{b.discount_pct}%)</p>
      <button className="btn-outline" data-testid={`bundle-add-${b.slug}`} disabled={b.items.some((i) => !i.in_stock)}
        onClick={() => { b.items.forEach((i) => addToCart(i.default_sku, i.qty, i.slug)); toast.success(t("added_cart")); }}>{t("add_bundle")}</button>
    </div>
  );
};

export default function Home() {
  const { t, lang } = useI18n();
  const { settings } = useOutletContext();
  const { data: cats } = useFetch(`/categories?lang=${lang}`, { cacheable: true });
  const { data: best } = useFetch(`/products?sort=bestsellers&limit=10&lang=${lang}`);
  const { data: sale } = useFetch(`/products?sale=true&sort=discount&limit=4&lang=${lang}`);
  const { data: cols } = useFetch(`/collections?lang=${lang}`, { cacheable: true });
  const { data: bundles } = useFetch(`/bundles?lang=${lang}`);
  const { data: guides } = useFetch(`/guides?lang=${lang}`);
  const hero = settings?.hero;
  const rooms = useMemo(() => (cols || []).filter((c) => c.kind === "room"), [cols]);
  const styles = useMemo(() => (cols || []).filter((c) => c.kind === "style"), [cols]);
  const cat = (s) => cats?.find((c) => c.slug === s);
  const trust = [[Truck, "trust_delivery", "/shipping"], [RotateCcw, "trust_returns", "/returns"], [ShieldCheck, "trust_pay", "/terms"], [Ruler, "trust_quality", "/care"], [MessageCircle, "trust_support", "/contact"]];
  return (
    <>
      <Seo title={null} description={hero?.subline} image={hero?.image} jsonLd={{ "@context": "https://schema.org", "@type": "Organization", name: "ANEL Furniture", logo: `${window.location.origin}/logo.png`, url: window.location.origin }} />
      <section className="relative" data-testid="home-hero">
        <div className="relative h-[72vh] min-h-[480px] max-h-[760px] overflow-hidden bg-[color:var(--hw-beige)]">
          {hero && <img src={hero.image} alt="" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />
          <div className="container-hw relative h-full flex flex-col justify-end pb-14 sm:pb-20">
            {hero?.promo && <span className="badge-sale self-start mb-4">{hero.promo}</span>}
            <h1 className="text-white text-5xl sm:text-6xl lg:text-7xl font-medium max-w-2xl leading-[1.02] fade-up">{hero?.headline}</h1>
            <p className="text-white/90 text-base sm:text-lg max-w-lg mt-5 fade-up" style={{ animationDelay: "100ms" }}>{hero?.subline}</p>
            <div className="flex flex-wrap gap-3 mt-8 fade-up" style={{ animationDelay: "200ms" }}>
              <Link to="/products" className="btn bg-white text-[color:var(--hw-ink)] hover:bg-[color:var(--hw-beige)]" data-testid="hero-shop-furniture-button">{t("shop_furniture")}</Link>
              <Link to="/c/bedroom" className="btn border border-white text-white hover:bg-white hover:text-[color:var(--hw-ink)]" data-testid="hero-explore-bedroom-button">{t("explore_bedroom")}</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-hw section" data-testid="home-categories">
        <SectionHead title={t("shop_by_category")} link="/products" linkLabel={t("view_all")} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-5">
          {TILES.map((s) => cat(s) && <Tile key={s} to={`/c/${s}`} img={cat(s).image} title={cat(s).name} testid={`category-tile-${s}`} />)}
        </div>
      </section>

      <section className="container-hw pb-16" data-testid="home-bestsellers">
        <SectionHead title={t("best_sellers")} link="/bestsellers" linkLabel={t("view_all")} />
        {best ? <ProductRail items={best.items} /> : <Loading />}
      </section>

      <section className="bg-[color:var(--hw-beige)] section" data-testid="home-rooms">
        <div className="container-hw">
          <SectionHead title={t("shop_by_room")} />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-5">
            {rooms.map((c, i) => <Tile key={c.slug} to={`/room/${c.slug}`} img={c.image} title={c.name} tall={i % 3 === 0} testid={`room-tile-${c.slug}`} />)}
          </div>
        </div>
      </section>

      {sale?.items?.length > 0 && (
        <section className="container-hw section" data-testid="home-offers">
          <SectionHead title={t("special_offers")} link="/sale" linkLabel={t("view_all")} />
          <ProductGrid items={sale.items} />
        </section>
      )}

      <section className="container-hw pb-16" data-testid="home-styles">
        <SectionHead title={t("shop_by_style")} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-5">
          {styles.map((c) => <Tile key={c.slug} to={`/style/${c.slug}`} img={c.image} title={c.name} testid={`style-tile-${c.slug}`} />)}
        </div>
      </section>

      <section className="bg-white border-y border-[color:var(--hw-line)] section" data-testid="home-bundles">
        <div className="container-hw">
          <SectionHead title={t("complete_room")} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">{(bundles || []).map((b) => <BundleCard key={b.slug} b={b} />)}</div>
        </div>
      </section>

      <section className="container-hw section" data-testid="home-trust">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {trust.map(([I, k, to]) => (
            <Link key={k} to={to} className="group" data-testid={`trust-${k}`}>
              <I size={26} strokeWidth={1.4} className="mb-3 text-[color:var(--hw-green)]" />
              <p className="font-semibold text-sm mb-1 group-hover:underline">{t(k)}</p><p className="text-sm text-[color:var(--hw-muted)]">{t(`${k}_d`)}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="container-hw pb-20" data-testid="home-guides">
        <SectionHead title={t("guides")} link="/inspiration" linkLabel={t("view_all")} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {(guides || []).map((g) => (
            <Link key={g.slug} to={`/inspiration/${g.slug}`} className="group" data-testid={`guide-card-${g.slug}`}>
              <div className="aspect-[4/3] overflow-hidden mb-4"><img src={g.image} alt={g.title} loading="lazy" className="w-full h-full object-cover img-zoom" /></div>
              <h3 className="font-serif text-2xl leading-tight mb-2 group-hover:underline">{g.title}</h3><p className="text-sm text-[color:var(--hw-muted)]">{g.excerpt}</p>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
